import React from 'react';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import { useIntl } from '@atlassian/jira-intl';
import { TABLE_ROWS_PER_PAGE_LIMIT } from '../../common/ui/constants';
import { TableContainer } from '../../common/ui/styled';
import type { OnEditSchemeParam, PriorityScheme, PrioritySchemesPage } from '../../common/ui/types';
import { calculateRowsOnPage } from '../../common/utils';
import { messages } from './messages';
import { usePrioritySchemesTable } from './utils';

type PrioritySchemesTableProps = {
	isLoading: boolean;
	isLocked: boolean;
	onEditScheme: (param: OnEditSchemeParam) => void;
	setDeleteModalOpen: (priorityScheme: PriorityScheme) => void;
	onFetch: (page: number) => void;
	page: PrioritySchemesPage;
};

export const PrioritySchemesTable = ({
	isLoading,
	isLocked,
	onEditScheme,
	setDeleteModalOpen,
	onFetch,
	page,
}: PrioritySchemesTableProps) => {
	const { formatMessage } = useIntl();

	const { currentPage, items, totalItems } = page;

	const { startRow, endRow } = calculateRowsOnPage({
		rowsPerPage: TABLE_ROWS_PER_PAGE_LIMIT,
		selectedPage: currentPage,
		totalRows: totalItems,
	});

	const { head, rows } = usePrioritySchemesTable({
		isLocked,
		onEditScheme,
		setDeleteModalOpen,
		prioritySchemes: items,
	});

	return (
		<TableContainer>
			<DynamicTableStateless
				testId="admin-pages-priority-schemes.ui.priority-schemes-table.dynamic-table-stateless"
				head={head}
				emptyView={<h3>{formatMessage(messages.noSearchResults)}</h3>}
				rows={rows}
				rowsPerPage={TABLE_ROWS_PER_PAGE_LIMIT}
				totalRows={totalItems}
				isFixedSize
				isLoading={isLoading}
				page={currentPage}
				onSetPage={onFetch}
			/>
			{!isLoading && totalItems > 0 && startRow <= totalItems && (
				<p>
					{formatMessage(messages.paginatorSummary, {
						startRow,
						endRow,
						totalRows: totalItems,
					})}
				</p>
			)}
		</TableContainer>
	);
};
